import React, {useState, useCallback, useEffect, useRef} from 'react'
import {Button, DatePicker} from '@thryvlabs/maverick'
import dayjs from 'dayjs'
import Moment from 'moment'
import * as XLSX from 'xlsx'
import {toast} from 'react-toastify'
import GenericTable from '../components/shared/generic-table'
import Pagination from '../components/pagination'
import {useAuthClient} from '../utils/use-auth-client'
import {useUserRoles} from '../utils/use-user-roles'
import {getAuthByRole} from '../utils/get-screen-auth-by-role'
import {AUTHORIZED_ROLES_BY_SCREENS} from '../utils/constants'
import NotFoundPage from '../components/not-found-page'

const Chip = ({children}) => (
  <span
    style={{
      display: 'inline-block',
      background: '#f3f4f6',
      border: '1px solid #e3e3e3',
      borderRadius: '12px',
      padding: '2px 8px',
      margin: '2px 4px 2px 0',
      fontSize: '11px',
      color: '#333',
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
)

const renderList = arr => {
  if (!Array.isArray(arr) || arr.length === 0) return '-'
  return (
    <div style={{display: 'flex', flexWrap: 'wrap', maxWidth: '320px'}}>
      {arr.map((v, i) => (
        <Chip key={i}>{v}</Chip>
      ))}
    </div>
  )
}

function BundleCouponReport() {
  const {roles} = useUserRoles()
  const client = useAuthClient()
  const [fromDate, setFromDate] = useState(Moment().subtract(30, 'days'))
  const [toDate, setToDate] = useState(Moment())
  const [reportData, setReportData] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [recordsPerPage, setRecordsPerPage] = useState(10)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  // Snapshot of dates used for the most recent successful fetch — pagination
  // uses these so the user can't accidentally page through a partially-changed
  // date range without re-clicking Generate Report.
  const [activeRange, setActiveRange] = useState(null)

  const toIsoDate = d => {
    if (!d) return ''
    if (typeof d.format === 'function') return d.format('YYYY-MM-DD')
    return dayjs(d).format('YYYY-MM-DD')
  }

  const fetchReport = useCallback(
    async ({from, to, page, perPage}) => {
      const offset = (page - 1) * perPage
      const endpoint = `reports/bundle-coupon?from_date=${from}&to_date=${to}&limit=${perPage}&offset=${offset}`
      setIsLoading(true)
      try {
        const json = await client(endpoint)
        if (json && json.success === false) {
          throw new Error(json.error || 'Report request failed')
        }
        setReportData(json)
      } catch (err) {
        toast.error(err.message || 'Failed to generate report.')
      } finally {
        setIsLoading(false)
      }
    },
    [client],
  )

  // Re-fetch when the active page or page size changes (but only after the
  // user has run an initial report — `activeRange` gates the effect).
  const isFirstRun = useRef(true)
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false
      return
    }
    if (!activeRange) return
    fetchReport({
      from: activeRange.from,
      to: activeRange.to,
      page: currentPage,
      perPage: recordsPerPage,
    })
  }, [currentPage, recordsPerPage, activeRange, fetchReport])

  const handleGenerate = () => {
    if (!fromDate || !toDate) {
      toast.error('Please select both Start and End dates.')
      return
    }
    if (Moment(fromDate).isAfter(Moment(toDate))) {
      toast.error('Start date must be before End date.')
      return
    }
    if (Moment(toDate).isAfter(Moment().endOf('day'))) {
      toast.error('End date cannot be in the future.')
      return
    }
    const from = toIsoDate(fromDate)
    const to = toIsoDate(toDate)
    setActiveRange({from, to})
    setCurrentPage(1)
    fetchReport({from, to, page: 1, perPage: recordsPerPage})
  }

  const handleExport = async () => {
    if (!activeRange) {
      toast.error('Generate a report first.')
      return
    }
    setIsExporting(true)
    try {
      // API caps `limit` at 1000, so we page through the full result set in
      // 1000-row chunks until we've collected everything.
      const EXPORT_CHUNK = 1000
      const MAX_CHUNKS = 100 // hard safety stop (100k rows)
      const all = []
      let offset = 0
      let total = Infinity
      for (let i = 0; i < MAX_CHUNKS; i++) {
        const endpoint = `reports/bundle-coupon?from_date=${activeRange.from}&to_date=${activeRange.to}&limit=${EXPORT_CHUNK}&offset=${offset}`
        const json = await client(endpoint)
        if (json && json.success === false) {
          throw new Error(json.error || 'Export request failed')
        }
        const chunk = (json && json.data) || []
        all.push(...chunk)
        if (typeof json?.total === 'number') total = json.total
        offset += EXPORT_CHUNK
        if (all.length >= total || chunk.length < EXPORT_CHUNK) break
      }
      const data = all
      if (data.length === 0) {
        toast.error('No data to export for the selected range.')
        return
      }
      const rows = data.map(row => ({
        Email: row.email || '',
        'Order ID': row.order_id || '',
        'Bundle Coupon Codes': (row.bundle_coupon_codes || []).join(', '),
        'Other Coupon Codes': (row.other_coupon_codes || []).join(', '),
        'Plan Codes': (row.plan_codes || []).join(', '),
        'Add-on Plan Codes': (row.addon_plan_codes || []).join(', '),
        'Updated Date': row.updated_date
          ? dayjs(row.updated_date).format('YYYY-MM-DD')
          : '',
        'Coupon Applied Correctly?': row.correct_combination || '',
      }))
      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Coupon Report')
      const filename = `coupon-report_${activeRange.from}_to_${activeRange.to}.xlsx`
      XLSX.writeFile(wb, filename)
    } catch (err) {
      toast.error(err.message || 'Failed to export report.')
    } finally {
      setIsExporting(false)
    }
  }

  const renderCorrectCombination = value => {
    if (!value) return '-'
    const isYes = String(value).toLowerCase() === 'yes'
    return (
      <span
        style={{
          display: 'inline-block',
          padding: '2px 10px',
          borderRadius: '12px',
          fontSize: '11px',
          fontWeight: 600,
          background: isYes ? '#e6f7ec' : '#fdecec',
          color: isYes ? '#1e7a3a' : '#b3261e',
          border: `1px solid ${isYes ? '#bfe6cc' : '#f3c0bd'}`,
          whiteSpace: 'nowrap',
        }}
      >
        {value}
      </span>
    )
  }

  const columns = [
    {key: 'email', label: 'Email'},
    {key: 'order_id', label: 'Order ID'},
    {
      key: 'bundle_coupon_codes',
      label: 'Bundle Coupon Codes',
      render: value => renderList(value),
    },
    {
      key: 'other_coupon_codes',
      label: 'Other Coupon Codes',
      render: value => renderList(value),
    },
    {
      key: 'plan_codes',
      label: 'Plan Codes',
      render: value => renderList(value),
    },
    {
      key: 'addon_plan_codes',
      label: 'Add-on Plan Codes',
      render: value => renderList(value),
    },
    {
      key: 'updated_date',
      label: 'Updated Date',
      render: value => (value ? dayjs(value).format('YYYY-MM-DD') : '-'),
    },
    {
      key: 'correct_combination',
      label: 'Coupon Applied Correctly?',
      render: value => renderCorrectCombination(value),
    },
  ]

  const total = reportData?.total ?? 0
  const totalPages = total > 0 ? Math.ceil(total / recordsPerPage) : 0
  const displayStart = total ? (currentPage - 1) * recordsPerPage + 1 : 0
  const displayEnd = Math.min(currentPage * recordsPerPage, total)

  // Admin-only screen — non-admins get a 404 so the route isn't enumerable.
  if (!getAuthByRole(AUTHORIZED_ROLES_BY_SCREENS.bundleCouponReport, roles)) {
    return <NotFoundPage />
  }

  return (
    <div style={{padding: '24px'}}>
      <h3
        className="control-center-item-header"
        style={{marginBottom: '8px', fontSize: '22px'}}
      >
        Coupon Report
      </h3>
      <p style={{color: '#6F6F6F', marginBottom: '16px', fontSize: '13px'}}>
        Select a date range and generate the report on-demand. Results can be
        exported to Excel.
      </p>

      {/* Filters */}
      <div
        style={{
          padding: '16px',
          backgroundColor: '#f9f9f9',
          borderRadius: '8px',
          border: '1px solid #e9e9e9',
          display: 'flex',
          gap: '24px',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          marginBottom: '16px',
        }}
      >
        <div style={{minWidth: '420px'}}>
          <DatePicker
            variant="singleRange"
            from={fromDate}
            to={toDate}
            setFrom={setFromDate}
            setTo={setToDate}
            name="bundle-coupon-range"
            fromLabel="Start Date"
            toLabel="End Date"
            format=""
            selectableDates="current/past"
          />
        </div>

        <div style={{display: 'flex', gap: '12px'}}>
          <Button
            variant="primary"
            onClick={handleGenerate}
            disabled={isLoading}
          >
            {isLoading ? 'Generating…' : 'Generate Report'}
          </Button>
          <Button
            variant="secondary"
            onClick={handleExport}
            disabled={
              isExporting || !reportData || (reportData?.total ?? 0) === 0
            }
          >
            {isExporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        </div>
      </div>

      {/* Results */}
      {reportData && (
        <>
          {total > 0 && (
            <h4
              className="control-center-display-text"
              style={{marginBottom: '12px', fontSize: '13px', color: '#4d4d4d'}}
            >
              Displaying {displayStart}-{displayEnd} out of {total} (
              {activeRange?.from} → {activeRange?.to})
            </h4>
          )}
          <div
            className="control-center-table-wrapper"
            style={{overflowX: 'auto'}}
          >
            <GenericTable
              columns={columns}
              rows={reportData.data || []}
              emptyMessage="No results for the selected date range."
              isLoading={isLoading}
              loadingMessage="Loading report…"
              skeletonRows={5}
            />
          </div>
          {total > 0 && (
            <Pagination
              totalRecords={total}
              recordsPerPage={recordsPerPage}
              setCurrentPage={setCurrentPage}
              currentPage={currentPage}
              setRecordsPerPage={value => {
                setRecordsPerPage(value)
                setCurrentPage(1)
              }}
              totalPages={totalPages}
            />
          )}
        </>
      )}
    </div>
  )
}

export {BundleCouponReport}
